/**
 * Refusals from the accommodation catalogue API, turned into something readable.
 *
 * The backend answers a refusal with HTTP 422 and a machine readable
 * `error_code` rather than 403, because the panel's interceptor signs the user
 * out on any 403. Without a mapping the page would show
 * "Request failed with status code 422".
 *
 * The raw server exception text is deliberately never shown: it is an internal
 * detail, and on an unexpected failure the endpoints do not send one anyway.
 */

export const ACCOMMODATION_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  insufficient_role: 'Your role cannot change the accommodation catalogue.',
  invalid_google_maps_link: 'The Google Maps link must be a plain https address, without credentials or spaces.',
  duplicate_accommodation: 'This resort already has an accommodation with the same name and address.',
  invalid_payload: 'The server refused one of the values sent. Shorten the search text, or check the coordinates and the page size.',
  field_not_writable: 'The request carried a field this form cannot write. Reload the page and try again.',
  invalid_coordinates: 'Latitude must be between -90 and 90, longitude between -180 and 180.',
  rate_limited: 'Too many address lookups in a row. Wait a minute and try again.',
};

interface ErrorLikeResponse {
  response?: {
    status?: number;
    data?: { error_code?: unknown; exc_type?: unknown; detail?: { reason?: unknown } };
  };
}

export const getAccommodationErrorCode = (error: unknown): string | null => {
  const candidate = (error as ErrorLikeResponse)?.response?.data?.error_code;
  return typeof candidate === 'string' && candidate ? candidate : null;
};

/** The server's own explanation, when it sent one alongside the code. */
export const getAccommodationErrorReason = (error: unknown): string | null => {
  const candidate = (error as ErrorLikeResponse)?.response?.data?.detail?.reason;
  return typeof candidate === 'string' && candidate ? candidate : null;
};

export const describeAccommodationError = (error: unknown, fallback: string): string => {
  const code = getAccommodationErrorCode(error);
  const reason = getAccommodationErrorReason(error);
  if (code) {
    const message = ACCOMMODATION_ERROR_MESSAGES[code] || `${fallback} (${code})`;
    // The endpoints send `detail.reason` when a code covers more than one mistake,
    // and a generic sentence about "some value" is not an answer the admin can act on.
    return reason ? `${message} ${reason}` : message;
  }

  const status = (error as ErrorLikeResponse)?.response?.status;
  if (status === 404) {
    return 'This accommodation no longer exists. Reload the page.';
  }
  if (status === 405) {
    return 'The panel called this endpoint the wrong way. Reload the page.';
  }

  return fallback;
};
